// src/pages/WebShop/WebShopOrdersOverviewPage.jsx
// =============================================================================
// Web Shop Orders Overview
// =============================================================================
//
// PURPOSE
// -------
// This page lists orders created by the public Web Shop checkout/payment flow.
// Internal users do not create these records manually. The page is therefore an
// operational overview and support cockpit, not an order-entry form.
//
// WHY THE LAYOUT MATCHES PRODUCT / VOUCHER OVERVIEWS
// --------------------------------------------------
// The hb_visits application already has a stabilized overview pattern for pages
// such as ProductOverview and VoucherOverview:
//
//   page root = fixed app-work-area height
//   filters/header = normal non-scrolling content
//   table wrapper = flex child owning the remaining height
//   table scroller = the only vertical/horizontal table scroll area
//   pagination footer = normal child at the bottom of the table wrapper
//
// This page follows the same pattern. We intentionally avoid scrolling the whole
// browser page because that makes pagination drift away from the table and looks
// inconsistent with the other mature overview screens.
//
// ACCESS MODEL
// ------------
// - All authenticated roles can read Web Shop orders.
// - Only superusers can trigger the voucher email resend action.
//
// The frontend mirrors this for UX clarity only. Backend authorization remains
// the real security boundary.
//
// PAGINATION / TOTALS
// -------------------
// The backend returns server-side count and amount totals for the filtered
// result set. The shared PaginationFooter displays:
// - rows-per-page selector,
// - current page / total pages,
// - order count,
// - total amount for all filtered orders,
// - amount for the current page.
//
// Pagination size changes are intentionally handled defensively. The shared
// PaginationFooter currently passes a numeric page size, but this page accepts
// both numeric values and DOM change events so future component changes do not
// break the 50 -> 20 / 20 -> 50 switching behavior.

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AuthContext from "../../context/AuthContext";
import PaginationFooter from "../../components/common/PaginationFooter";
import {
  listShopOrders,
  resendShopVoucherEmail,
} from "../../services/shopAdminService";

import "./WebShopOrdersOverviewPage.css";

const INITIAL_FILTERS = {
  q: "",
  status: "",
  emailStatus: "",
};

const PAGE_SIZE_OPTIONS = [20, 50, 100];

function normalizeRole(role) {
  return String(role || "").trim().toLowerCase();
}

function formatMoney(amount, currency) {
  const n = Number(amount || 0);
  const cur = currency || "PLN";

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: cur,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${cur}`;
  }
}

function formatDate(value) {
  if (!value) return "—";

  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

function badgeClass(value) {
  const v = String(value || "").toLowerCase();

  if (["paid", "sent"].includes(v)) {
    return "web-shop-orders-badge web-shop-orders-badge--paid";
  }

  if (["pending", "queued"].includes(v)) {
    return "web-shop-orders-badge web-shop-orders-badge--pending";
  }

  if (["failed", "error", "cancelled"].includes(v)) {
    return "web-shop-orders-badge web-shop-orders-badge--failed";
  }

  return "web-shop-orders-badge";
}

function hasManualResendState(order) {
  const status = String(order?.voucherEmailResend?.status || "none").toLowerCase();
  return status && status !== "none";
}

function safePageNumber(offset, limit) {
  return Math.floor(Number(offset || 0) / Math.max(1, Number(limit || 1))) + 1;
}

function normalizePageSizeValue(value, fallback = 50) {
  /*
    PaginationFooter currently calls onPageSizeChange(nextNumber), but accepting
    an event-like object makes this overview safer if the shared component is
    adjusted in the future. This prevents a subtle failure mode where changing
    rows-per-page from 50 to 20 would accidentally store NaN or an event object
    as the page size.
  */
  const raw = value && value.target ? value.target.value : value;
  const parsed = Number(raw);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback;
  }

  return parsed;
}

export default function WebShopOrdersOverviewPage() {
  const { t } = useTranslation(["sales", "common"]);
  const { authState } = useContext(AuthContext) || {};

  const role = normalizeRole(authState?.user?.role);
  const canResendVoucherEmail = role === "superuser";

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  const [amountTotal, setAmountTotal] = useState(0);
  const [pageAmountTotal, setPageAmountTotal] = useState(0);
  const [currency, setCurrency] = useState("PLN");

  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  /**
   * Server-side query parameters.
   *
   * Web Shop orders can grow over time, therefore this overview deliberately
   * uses backend pagination instead of client-side pagination. The UI still
   * follows the same PaginationFooter pattern as ProductOverview; only the data
   * source differs.
   */
  const queryParams = useMemo(() => {
    const params = { limit, offset };

    if (appliedFilters.q.trim()) params.q = appliedFilters.q.trim();
    if (appliedFilters.status) params.status = appliedFilters.status;
    if (appliedFilters.emailStatus) {
      params.emailStatus = appliedFilters.emailStatus;
    }

    return params;
  }, [appliedFilters, limit, offset]);

  const pageNumber = safePageNumber(offset, limit);
  const pageCount = Math.max(
    1,
    Math.ceil(Number(count || 0) / Math.max(1, Number(limit || 1)))
  );

  const displayedFrom = count > 0 ? offset + 1 : 0;
  const displayedTo = Math.min(offset + rows.length, count);

  async function loadOrders() {
    setLoading(true);
    setError(null);

    try {
      const result = await listShopOrders(queryParams);

      setRows(result.rows || []);
      setCount(Number(result.count || 0));
      setAmountTotal(Number(result.amountTotal || 0));
      setPageAmountTotal(Number(result.pageAmountTotal || 0));
      setCurrency(result.currency || "PLN");
    } catch (err) {
      console.error("[WebShopOrdersOverviewPage] loadOrders failed", err);
      setError(t("sales:shopOrders.messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadOrders();
    // queryParams is memoized, so this effect runs only when filters or
    // pagination change. loadOrders stays local to keep the code readable.

  }, [queryParams]);

  useEffect(() => {
    if (!error && !message) return;

    const timer = setTimeout(() => {
      setError(null);
      setMessage(null);
    }, 6500);

    return () => clearTimeout(timer);
  }, [error, message]);

  function updateFilter(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }

  function applyFilters() {
    setOffset(0);
    setAppliedFilters(filters);
  }

  function clearFilters() {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    setOffset(0);
  }

  function goToPreviousPage() {
    setOffset((prev) => Math.max(0, Number(prev || 0) - Number(limit || 50)));
  }

  function goToNextPage() {
    setOffset((prev) => {
      const next = Number(prev || 0) + Number(limit || 50);
      return next >= count ? prev : next;
    });
  }

  function changeLimit(nextLimit) {
    const nextPageSize = normalizePageSizeValue(nextLimit, 50);

    // Always jump back to the first page when the page size changes. This is
    // the same user-facing behavior used by mature overview pages: switching
    // 50 -> 20 records should immediately reload the first 20 rows instead of
    // trying to preserve an offset that may no longer represent the intended
    // page.
    setLimit(nextPageSize);
    setOffset(0);
  }

  async function resendEmail(order) {
    if (!canResendVoucherEmail) return;

    const ok = window.confirm(
      t("sales:shopOrders.messages.resendConfirm", { id: order.id })
    );
    if (!ok) return;

    setActionId(order.id);
    setError(null);
    setMessage(null);

    try {
      const result = await resendShopVoucherEmail(order.id, {
        // Important: do not force queue mode from the UI. The backend knows
        // whether the current installation sends shop emails inline or through
        // BullMQ, and the manual resend must follow the same configured path.
        deliveryMode: "auto",
      });

      const messageKey =
        result?.deliveryMode === "queue"
          ? "sales:shopOrders.messages.resendQueued"
          : "sales:shopOrders.messages.resendSent";

      setMessage(t(messageKey, { id: order.id }));
      await loadOrders();
    } catch (err) {
      console.error("[WebShopOrdersOverviewPage] resendEmail failed", err);
      setError(t("sales:shopOrders.messages.resendFailed"));
    } finally {
      setActionId(null);
    }
  }

  const footerSummary = (
    <div className="web-shop-orders-pagination-summary">
      <span className="web-shop-orders-total-chip">
        <em>
          {t("sales:shopOrders.pagination.range", {
            from: displayedFrom,
            to: displayedTo,
            count,
          })}
        </em>
      </span>
      <span className="web-shop-orders-total-chip">
        <em>{t("sales:shopOrders.pagination.orders", { count })}</em>
      </span>
      <span className="web-shop-orders-total-chip">
        <em>
          {t("sales:shopOrders.pagination.amountTotal", {
            amount: formatMoney(amountTotal, currency),
          })}
        </em>
      </span>
      <span className="web-shop-orders-total-chip">
        <em>
          {t("sales:shopOrders.pagination.pageAmount", {
            amount: formatMoney(pageAmountTotal, currency),
          })}
        </em>
      </span>
    </div>
  );

  return (
    <main className="web-shop-orders-page">
      <header className="web-shop-orders-header">
        <div>
          <p className="web-shop-orders-eyebrow">
            {t("sales:shopOrders.eyebrow")}
          </p>
          <h1 className="web-shop-orders-title">
            {t("sales:shopOrders.title")}
          </h1>
          <p className="web-shop-orders-subtitle">
            {t("sales:shopOrders.subtitle")}
          </p>
        </div>

        <button
          type="button"
          className="web-shop-orders-button"
          onClick={loadOrders}
          disabled={loading}
        >
          {t("common:refresh", { defaultValue: "Refresh" })}
        </button>
      </header>

      <section className="web-shop-orders-guidance">
        {t("sales:shopOrders.guidance")}
      </section>

      {error && <div className="web-shop-orders-alert web-shop-orders-alert--error">{error}</div>}
      {message && <div className="web-shop-orders-alert web-shop-orders-alert--success">{message}</div>}

      <section className="web-shop-orders-filters">
        <label className="web-shop-orders-field web-shop-orders-field--wide">
          <span className="web-shop-orders-label">
            {t("sales:shopOrders.filters.search")}
          </span>
          <input
            className="web-shop-orders-input"
            value={filters.q}
            onChange={(e) => updateFilter("q", e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilters();
            }}
            placeholder={t("sales:shopOrders.filters.searchPlaceholder")}
          />
        </label>

        <label className="web-shop-orders-field">
          <span className="web-shop-orders-label">
            {t("sales:shopOrders.filters.status")}
          </span>
          <select
            className="web-shop-orders-select"
            value={filters.status}
            onChange={(e) => updateFilter("status", e.target.value)}
          >
            <option value="">{t("sales:shopOrders.filters.all")}</option>
            {["pending", "paid", "cancelled", "error"].map((status) => (
              <option key={status} value={status}>
                {t(`sales:shopOrders.statuses.${status}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="web-shop-orders-field">
          <span className="web-shop-orders-label">
            {t("sales:shopOrders.filters.emailStatus")}
          </span>
          <select
            className="web-shop-orders-select"
            value={filters.emailStatus}
            onChange={(e) => updateFilter("emailStatus", e.target.value)}
          >
            <option value="">{t("sales:shopOrders.filters.all")}</option>
            {["pending", "queued", "sent", "failed"].map((status) => (
              <option key={status} value={status}>
                {t(`sales:shopOrders.emailStatuses.${status}`)}
              </option>
            ))}
          </select>
        </label>

        <div className="web-shop-orders-filter-actions">
          <button
            type="button"
            className="web-shop-orders-button web-shop-orders-button--primary"
            onClick={applyFilters}
            disabled={loading}
          >
            {t("sales:shopOrders.filters.apply")}
          </button>
          <button
            type="button"
            className="web-shop-orders-button"
            onClick={clearFilters}
            disabled={loading}
          >
            {t("sales:shopOrders.filters.clear")}
          </button>
        </div>
      </section>

      <section className="web-shop-orders-table-wrap">
        <div
          className={`web-shop-orders-table-scroller ${loading ? "is-loading" : ""}`}
          aria-busy={loading}
        >
          <div className="web-shop-orders-table-x">
            <table className="web-shop-orders-table">
              <thead>
                <tr>
                  <th>{t("sales:shopOrders.columns.order")}</th>
                  <th>{t("sales:shopOrders.columns.buyer")}</th>
                  <th>{t("sales:shopOrders.columns.amount")}</th>
                  <th>{t("sales:shopOrders.columns.status")}</th>
                  <th>{t("sales:shopOrders.columns.voucher")}</th>
                  <th>{t("sales:shopOrders.columns.email")}</th>
                  <th>{t("sales:shopOrders.columns.manualResend")}</th>
                  <th>{t("sales:shopOrders.columns.created")}</th>
                  <th>{t("sales:shopOrders.columns.actions")}</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((order) => (
                  <tr key={order.id}>
                    <td>
                      <div className="web-shop-orders-cell-title">#{order.id}</div>
                      <div className="web-shop-orders-cell-muted">
                        {order.product_code || "—"}
                      </div>
                      <div className="web-shop-orders-cell-muted">
                        {order.template_version || "—"}
                      </div>
                    </td>

                    <td>
                      <div className="web-shop-orders-cell-title">
                        {order.buyer_name || "—"}
                      </div>
                      <div className="web-shop-orders-cell-muted">
                        {order.buyer_email || "—"}
                      </div>
                      {order.recipient_name && (
                        <div className="web-shop-orders-cell-muted">
                          {t("sales:shopOrders.recipient")}: {order.recipient_name}
                        </div>
                      )}
                    </td>

                    <td className="web-shop-orders-amount-cell">
                      {formatMoney(order.amount, order.currency)}
                    </td>

                    <td>
                      <span className={badgeClass(order.status)}>
                        {t(`sales:shopOrders.statuses.${order.status}`, {
                          defaultValue: order.status,
                        })}
                      </span>
                      <div className="web-shop-orders-cell-muted">
                        {formatDate(order.paid_at)}
                      </div>
                    </td>

                    <td>
                      {order.voucher_id ? (
                        <>
                          <div className="web-shop-orders-cell-title">
                            #{order.voucher_id}
                          </div>
                          <div className="web-shop-orders-cell-muted">
                            {order.voucher?.voucher_no || "—"}
                          </div>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td>
                      <span className={badgeClass(order.email?.status)}>
                        {t(`sales:shopOrders.emailStatuses.${order.email?.status}`, {
                          defaultValue: order.email?.status || "—",
                        })}
                      </span>
                      <div className="web-shop-orders-cell-muted">
                        {t("sales:shopOrders.attempts", {
                          count: order.email?.attempts || 0,
                        })}
                      </div>
                      {order.email?.last_error && (
                        <div
                          className="web-shop-orders-cell-muted web-shop-orders-error-preview"
                          title={order.email.last_error}
                        >
                          {String(order.email.last_error).slice(0, 80)}
                        </div>
                      )}
                    </td>

                    <td>
                      {hasManualResendState(order) ? (
                        <>
                          <span className={badgeClass(order.voucherEmailResend?.status)}>
                            {t(
                              `sales:shopOrders.manualResendStatuses.${order.voucherEmailResend?.status}`,
                              {
                                defaultValue: order.voucherEmailResend?.status || "—",
                              }
                            )}
                          </span>
                          <div className="web-shop-orders-cell-muted">
                            {t("sales:shopOrders.attempts", {
                              count: order.voucherEmailResend?.attempts || 0,
                            })}
                          </div>
                          {order.voucherEmailResend?.delivery_mode && (
                            <div className="web-shop-orders-cell-muted">
                              {t("sales:shopOrders.deliveryMode", {
                                mode: order.voucherEmailResend.delivery_mode,
                              })}
                            </div>
                          )}
                          {order.voucherEmailResend?.last_error && (
                            <div
                              className="web-shop-orders-cell-muted web-shop-orders-error-preview"
                              title={order.voucherEmailResend.last_error}
                            >
                              {String(order.voucherEmailResend.last_error).slice(0, 80)}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="web-shop-orders-cell-muted">
                          {t("sales:shopOrders.manualResendStatuses.none")}
                        </span>
                      )}
                    </td>

                    <td>{formatDate(order.createdAt)}</td>

                    <td>
                      {canResendVoucherEmail ? (
                        <button
                          type="button"
                          className="web-shop-orders-link-button"
                          onClick={() => resendEmail(order)}
                          disabled={actionId === order.id || order.status !== "paid"}
                          title={
                            order.status !== "paid"
                              ? t("sales:shopOrders.actions.onlyPaid")
                              : undefined
                          }
                        >
                          {actionId === order.id
                            ? t("sales:shopOrders.actions.resending")
                            : t("sales:shopOrders.actions.resendVoucherEmail")}
                        </button>
                      ) : (
                        <span className="web-shop-orders-readonly-action">
                          {t("sales:shopOrders.actions.readOnly")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}

                {!rows.length && (
                  <tr>
                    <td colSpan="9" className="web-shop-orders-empty">
                      {loading
                        ? t("common:loading", { defaultValue: "Loading…" })
                        : t("sales:shopOrders.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <PaginationFooter
          className="web-shop-orders-pagination"
          page={pageNumber}
          pageCount={pageCount}
          pageSize={limit}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          totalRows={count}
          disabled={loading}
          onPageSizeChange={changeLimit}
          onPrev={goToPreviousPage}
          onNext={goToNextPage}
          summary={footerSummary}
        />
      </section>
    </main>
  );
}
